import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Row, Space, Statistic, Table, Tag, message } from 'antd';
import {
  CheckCircleOutlined,
  FileTextOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import dermatoApi, { DermatoForm, FormStatus } from '../../api/dermatoApi';
import hepatoApi, { HepatoForm, HepatoFormStatus } from '../../api/hepatoApi';
import { patientApi, Patient } from '../../api/patientApi';
import { useAuthStore } from '../../store/authStore';
import { canUseDermato, canUseHepato, getRoleProgramType } from '../../utils/flowAccess';
import { PHASE_LABELS } from '../../utils/flowLabels';
import { features } from '../../config/features';

type QuestionnaireType = 'DERMATO' | 'HEPATO';
type QuestionnaireStatus = FormStatus | HepatoFormStatus;

type DashboardForm = {
  id: string;
  patientId: string;
  type: QuestionnaireType;
  status: QuestionnaireStatus;
  summary: string;
  createdAt: string;
  updatedAt: string;
};

type DashboardStats = {
  totalPatients: number;
  dermatoSubmitted: number;
  hepatoSubmitted: number;
  drafts: number;
  today: number;
};

const initialStats: DashboardStats = {
  totalPatients: 0,
  dermatoSubmitted: 0,
  hepatoSubmitted: 0,
  drafts: 0,
  today: 0,
};

const emptyFormPage = {
  data: [],
  total: 0,
  page: 1,
  limit: 0,
};

const statusLabel: Record<QuestionnaireStatus, string> = {
  DRAFT: 'Ciornă',
  SUBMITTED: 'Trimis',
  REVIEWED: 'Revizuit',
  ARCHIVED: 'Arhivat',
};

const statusColor: Record<QuestionnaireStatus, string> = {
  DRAFT: 'default',
  SUBMITTED: 'blue',
  REVIEWED: 'green',
  ARCHIVED: 'purple',
};

const submittedTotal = (submitted: number, reviewed: number) => submitted + reviewed;

const toDermatoDashboardForm = (form: DermatoForm): DashboardForm => ({
  id: form.id,
  patientId: form.patientId,
  type: 'DERMATO',
  status: form.status,
  summary: form.phase ? PHASE_LABELS[form.phase] ?? statusLabel[form.status] : statusLabel[form.status],
  createdAt: form.createdAt,
  updatedAt: form.updatedAt,
});

const toHepatoDashboardForm = (form: HepatoForm): DashboardForm => ({
  id: form.id,
  patientId: form.patientId,
  type: 'HEPATO',
  status: form.status,
  summary: form.phase ? PHASE_LABELS[form.phase] ?? statusLabel[form.status] : statusLabel[form.status],
  createdAt: form.createdAt,
  updatedAt: form.updatedAt,
});

const DashboardPage = () => {
  const navigate = useNavigate();
  const roleName = useAuthStore((state) => state.user?.role?.name);
  const roleProgramType = getRoleProgramType(roleName);
  const allowDermato = canUseDermato(roleName);
  const allowHepato = canUseHepato(roleName);
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [forms, setForms] = useState<DashboardForm[]>([]);
  const [stats, setStats] = useState<DashboardStats>(initialStats);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const todayStart = dayjs().startOf('day').toISOString();
      const todayEnd = dayjs().endOf('day').toISOString();

      const [
        patientsResult,
        dermatoRecent,
        hepatoRecent,
        dermatoSubmitted,
        dermatoReviewed,
        hepatoSubmitted,
        hepatoReviewed,
        dermatoDrafts,
        hepatoDrafts,
        dermatoToday,
        hepatoToday,
      ] = await Promise.all([
        patientApi.list({ limit: 100, programType: roleProgramType }),
        allowDermato ? dermatoApi.list({ limit: 100, sortBy: 'updatedAt', sortOrder: 'desc' }) : Promise.resolve(emptyFormPage),
        allowHepato ? hepatoApi.list({ limit: 100, sortBy: 'updatedAt', sortOrder: 'desc' }) : Promise.resolve(emptyFormPage),
        allowDermato ? dermatoApi.list({ limit: 1, status: FormStatus.SUBMITTED }) : Promise.resolve(emptyFormPage),
        allowDermato ? dermatoApi.list({ limit: 1, status: FormStatus.REVIEWED }) : Promise.resolve(emptyFormPage),
        allowHepato ? hepatoApi.list({ limit: 1, status: HepatoFormStatus.SUBMITTED }) : Promise.resolve(emptyFormPage),
        allowHepato ? hepatoApi.list({ limit: 1, status: HepatoFormStatus.REVIEWED }) : Promise.resolve(emptyFormPage),
        allowDermato ? dermatoApi.list({ limit: 1, status: FormStatus.DRAFT }) : Promise.resolve(emptyFormPage),
        allowHepato ? hepatoApi.list({ limit: 1, status: HepatoFormStatus.DRAFT }) : Promise.resolve(emptyFormPage),
        allowDermato ? dermatoApi.list({ limit: 1, createdAfter: todayStart, createdBefore: todayEnd }) : Promise.resolve(emptyFormPage),
        allowHepato ? hepatoApi.list({ limit: 1, createdAfter: todayStart, createdBefore: todayEnd }) : Promise.resolve(emptyFormPage),
      ]);

      setPatients(patientsResult.patients);
      setForms([
        ...dermatoRecent.data.map(toDermatoDashboardForm),
        ...hepatoRecent.data.map(toHepatoDashboardForm),
      ]);
      setStats({
        totalPatients: patientsResult.total,
        dermatoSubmitted: submittedTotal(dermatoSubmitted.total, dermatoReviewed.total),
        hepatoSubmitted: submittedTotal(hepatoSubmitted.total, hepatoReviewed.total),
        drafts: dermatoDrafts.total + hepatoDrafts.total,
        today: dermatoToday.total + hepatoToday.total,
      });
    } catch (error) {
      message.error('Eroare la incarcarea dashboard-ului');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [roleProgramType, allowDermato, allowHepato]);

  const patientById = useMemo(() => {
    return patients.reduce<Record<string, Patient>>((acc, patient) => {
      acc[patient.id] = patient;
      return acc;
    }, {});
  }, [patients]);

  const recentForms = [...forms]
    .sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf())
    .slice(0, 10);

  const openPatientFilter = (filter: string) => {
    navigate(`/patients?dashboardFilter=${filter}`);
  };

  const clickableCardStyle = {
    cursor: 'pointer',
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>Dashboard</h1>
        <Button icon={<ReloadOutlined />} onClick={loadDashboard} loading={loading}>
          Reincarca
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={5}>
          <Card
            hoverable
            role="button"
            tabIndex={0}
            aria-label="Deschide toti pacientii"
            style={clickableCardStyle}
            onClick={() => openPatientFilter('all')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openPatientFilter('all');
              }
            }}
          >
            <Statistic title="Total Pacienti" value={stats.totalPatients} prefix={<UserOutlined />} />
          </Card>
        </Col>
        {allowDermato && (
          <Col xs={24} sm={12} lg={5}>
            <Card
              hoverable
              role="button"
              tabIndex={0}
              aria-label="Deschide pacientii cu chestionare dermato trimise"
              style={clickableCardStyle}
              onClick={() => openPatientFilter('dermato-submitted')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openPatientFilter('dermato-submitted');
                }
              }}
            >
              <Statistic
                title="Dermato Trimise"
                value={stats.dermatoSubmitted}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
        )}
        {allowHepato && (
          <Col xs={24} sm={12} lg={5}>
            <Card
              hoverable
              role="button"
              tabIndex={0}
              aria-label="Deschide pacientii cu chestionare hepato trimise"
              style={clickableCardStyle}
              onClick={() => openPatientFilter('hepato-submitted')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openPatientFilter('hepato-submitted');
                }
              }}
            >
              <Statistic
                title="Hepato Trimise"
                value={stats.hepatoSubmitted}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
        )}
        <Col xs={24} sm={12} lg={4}>
          <Card
            hoverable
            role="button"
            tabIndex={0}
            aria-label="Deschide pacientii cu drafturi"
            style={clickableCardStyle}
            onClick={() => openPatientFilter('drafts')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openPatientFilter('drafts');
              }
            }}
          >
            <Statistic title="Drafturi" value={stats.drafts} prefix={<FileTextOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Card
            hoverable
            role="button"
            tabIndex={0}
            aria-label="Deschide pacientii cu chestionare create azi"
            style={clickableCardStyle}
            onClick={() => openPatientFilter('today')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openPatientFilter('today');
              }
            }}
          >
            <Statistic title="Azi" value={stats.today} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
      </Row>

      <Card title="Ultimele chestionare" style={{ marginTop: 24 }}>
        {forms.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message="Nu exista chestionare inca"
            description="Dupa trimiterea unei anamneze, aceasta va aparea aici."
          />
        ) : (
          <Table
            scroll={{ x: 'max-content' }}
            rowKey={(form) => `${form.type}-${form.id}`}
            loading={loading}
            dataSource={recentForms}
            pagination={false}
            columns={[
              {
                title: 'Pacient',
                key: 'patient',
                render: (_: unknown, form: DashboardForm) => {
                  const patient = patientById[form.patientId];
                  return patient ? `${patient.lastName} ${patient.firstName}` : form.patientId;
                },
              },
              {
                title: 'Tip',
                dataIndex: 'type',
                key: 'type',
                render: (type: QuestionnaireType) => (
                  <Tag color={type === 'HEPATO' ? 'gold' : 'blue'}>{type === 'HEPATO' ? 'Hepato' : 'Dermato'}</Tag>
                ),
              },
              {
                title: 'Rezumat',
                dataIndex: 'summary',
                key: 'summary',
                ellipsis: true,
                render: (summary: string) => summary.length > 50 ? `${summary.slice(0, 47)}...` : summary,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status: QuestionnaireStatus) => (
                  <Tag color={statusColor[status]}>{statusLabel[status]}</Tag>
                ),
              },
              {
                title: 'Actualizat',
                dataIndex: 'updatedAt',
                key: 'updatedAt',
                render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
              },
              {
                title: 'Actiuni',
                key: 'actions',
                render: (_: unknown, form: DashboardForm) => (
                  <Space>
                    {features.clinicalWorkflow ? (
                      <Button
                        onClick={() =>
                          navigate(
                            `/patients/${form.patientId}/${form.type === 'HEPATO' ? 'hepato-wizard' : 'dermato-wizard'}`,
                          )
                        }
                      >
                        Deschide
                      </Button>
                    ) : (
                      // Fără flux clinic: direct în spațiul de lucru (registrul importat, Faza 1);
                      // fișa administrativă a pacientului rămâne la un click distanță.
                      <>
                        <Button
                          onClick={() =>
                            navigate(`/patients/${form.patientId}/${form.type === 'HEPATO' ? 'hepato' : 'dermato'}`)
                          }
                        >
                          Vezi fișa
                        </Button>
                        <Button type="link" size="small" onClick={() => navigate(`/patients/${form.patientId}`)}>
                          Fișa pacient
                        </Button>
                      </>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
};

export default DashboardPage;
